import React, { useCallback, useMemo, useState } from "react";

import { IdentityProviderType } from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";
import { Divider, Modal, Select } from "@skydio/rivet-ui";

import UpdateAxonConnectProviderPane from "./UpdateAxonConnectProviderPane";
import UpdateOidcProviderPane from "./UpdateOidcProviderPane";
import UpdateSamlProviderPane from "./UpdateSamlProviderPane";

import type { ValueType } from "@skydio/core";
import type { Key } from "@skydio/rivet-ui";
import type { identityprovidertype } from "__generated__/OrgLoginMethods_loginMethods.graphql";

type page = "types" | "providerForm" | "results";

interface idp {
  id: string;
  uuid: string;
  name: string;
  providerType: identityprovidertype;
  oidcIssuer: string | null;
  oidcUsePkce: string | null;
  oidcClientId: string | null;
  axonConnectDomain: string | null;
}

export interface UpdateSsoModalContentProps {
  orgNodeId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
  connection?: string;
  idp: idp;
}

function mapProviderType(
  type: identityprovidertype | null | undefined
): ValueType<IdentityProviderType.IdentityProviderTypeEnumMap> | null {
  if (type === null || type === undefined || type === "%future added value") {
    return null;
  }
  const mapping: Record<
    identityprovidertype,
    ValueType<IdentityProviderType.IdentityProviderTypeEnumMap>
  > = {
    UNKNOWN: IdentityProviderType.IdentityProviderTypeEnum.UNKNOWN,
    "%future added value": IdentityProviderType.IdentityProviderTypeEnum.UNKNOWN, // Need to appease type checker
    SAML: IdentityProviderType.IdentityProviderTypeEnum.SAML,
    OIDC: IdentityProviderType.IdentityProviderTypeEnum.OIDC,
    AXON: IdentityProviderType.IdentityProviderTypeEnum.AXON,
  };
  return mapping[type];
}

export const UpdateSsoModalContent = ({
  isOpen,
  onOpenChange,
  onCompleted,
  idp,
}: UpdateSsoModalContentProps) => {
  const [currentPage, setCurrentPage] = useState<page>("types");
  const [selectedType, setSelectedType] =
    useState<ValueType<IdentityProviderType.IdentityProviderTypeEnumMap> | null>(
      mapProviderType(idp?.providerType) ?? null
    );
  const defaultType = useMemo(() => {
    return mapProviderType(idp?.providerType) ?? null;
  }, [idp]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange(open);
      if (!open) {
        // Clear everything when the modal is closed
        setCurrentPage("types");
        setSelectedType(defaultType);
      }
    },
    [defaultType, onOpenChange]
  );
  const handleClose = useCallback(() => {
    onCompleted?.();
    setSelectedType(defaultType);
    setCurrentPage("types");
  }, [defaultType, onCompleted]);

  const handleSelectionChange = useCallback((key: Key | null) => {
    setSelectedType(key as ValueType<IdentityProviderType.IdentityProviderTypeEnumMap>);
  }, []);

  const handlePaneUpdate = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal
      title="New SSO Provider"
      size="3xl"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onClose={handleClose}
    >
      <Modal.Content>
        {/* Set Type page in modal and include forms */}
        {currentPage === "types" && (
          <>
            <Modal.Header>
              <Modal.Title>Update {idp.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="mb-4">
              <Select
                className="w-5/6"
                aria-label="SSO Type"
                placeholder="SSO Type"
                isDisabled={true}
                items={Object.entries(IdentityProviderType.IdentityProviderTypeEnum)
                  .filter(k => {
                    return k[0] != "UNKNOWN";
                  })
                  .map(([key, enumValue]) => {
                    switch (key) {
                      case "AXON": // Pretty print Axon
                        return { key: enumValue, label: "Axon Connect" };
                      default:
                        return { key: enumValue, label: key };
                    }
                  })}
                onSingleSelectionChange={handleSelectionChange}
                selectedKey={selectedType?.toString()}
              >
                {item => <Select.Item key={item.key}>{item.label}</Select.Item>}
              </Select>
              {selectedType !== null && <Divider />}
              {selectedType == IdentityProviderType.IdentityProviderTypeEnum.SAML && (
                <UpdateSamlProviderPane
                  idp={idp}
                  onNext={handlePaneUpdate}
                  onCancel={handlePaneUpdate}
                />
              )}
              {selectedType == IdentityProviderType.IdentityProviderTypeEnum.OIDC && (
                <UpdateOidcProviderPane
                  idp={idp}
                  onNext={handlePaneUpdate}
                  onCancel={handlePaneUpdate}
                />
              )}
              {selectedType == IdentityProviderType.IdentityProviderTypeEnum.AXON && (
                <UpdateAxonConnectProviderPane
                  idp={idp}
                  onNext={handlePaneUpdate}
                  onCancel={handlePaneUpdate}
                />
              )}
            </Modal.Body>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

interface UpdateSsoModalProps {
  orgNodeId: string;
  onNew?: () => void;
  connection?: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  idp: idp;
}
const UpdateSsoModal = ({
  orgNodeId,
  onNew,
  connection,
  idp,
  isOpen,
  setIsOpen,
}: UpdateSsoModalProps) => {
  return (
    <>
      <UpdateSsoModalContent
        orgNodeId={orgNodeId}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onCompleted={onNew}
        connection={connection}
        idp={idp}
      />
    </>
  );
};

export default UpdateSsoModal;
